import { QueryClientProvider } from '@tanstack/react-query'
import { LogOut, Settings, SquareTerminal } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/ui/user-avatar'
import { appHref, INSURE_IN_THIS_APP } from '@/config/config'
import { useAuthStatus, useSignOut } from '@/models/auth'
import { insureQueryClient } from '@/models/insureQueryClient'
import { staffUserName, useCurrentUser } from '@/models/user'

const CONSOLE_PATH = '/insure/internal/customers'

/** The signed-in person: who they are, their settings, and the way out. */
export function AccountMenu() {
  const { user } = useCurrentUser({ enabled: false })
  const signOut = useSignOut()

  if (!user) return null

  const name = staffUserName(user)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account"
        className="focus-visible:ring-brand-700/50 cursor-pointer rounded-full outline-none focus-visible:ring-[3px]"
      >
        <UserAvatar name={name} src={user.photo} className="size-8" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="ph-no-capture flex flex-col gap-0.5">
          <span className="text-ink truncate text-sm font-medium">{name}</span>
          <span className="text-ink-500 truncate text-xs font-normal">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/insure/admin-settings">
            <Settings />
            Settings
          </Link>
        </DropdownMenuItem>
        {user.isCaiAdmin && (
          // Off Insure's origin, as on the legacy Bookkeeper host, the console is a page load away.
          <DropdownMenuItem asChild>
            {INSURE_IN_THIS_APP ? (
              <Link to={CONSOLE_PATH}>
                <SquareTerminal />
                Console
              </Link>
            ) : (
              <a href={appHref(CONSOLE_PATH)}>
                <SquareTerminal />
                Console
              </a>
            )}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => signOut.mutate()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The one account menu the product shell's bar shows on every page: Insure's, settings included, for any
 * account with an Insure user. An account without one has no Insure settings, so it keeps the fallback.
 */
export function ShellAccountMenu({ fallback }: { fallback?: ReactNode }) {
  return (
    <QueryClientProvider client={insureQueryClient}>
      <InsureAccountMenuOr fallback={fallback} />
    </QueryClientProvider>
  )
}

function InsureAccountMenuOr({ fallback }: { fallback?: ReactNode }) {
  const { data: authStatus } = useAuthStatus()
  const { user } = useCurrentUser({ enabled: !!authStatus?.authenticated })
  return user ? <AccountMenu /> : fallback
}
